import { useSelector } from "react-redux";
import { getCustomerIdFromFeedPost } from "../utils/getCustomerIdFromFeedPost";
import { filterOutFeedPostsByCondition } from "../utils/filterOutFeedPostsByCondition";

/**
 * @typedef {import("../types/feed-post").FeedPostStatus} FeedPostStatus
 * @typedef {import("../types/feed-post").FeedPostGeneric} FeedPostGeneric
 *
 * @typedef {Object} FeedPageFilters
 * @property {FeedPostStatus[]} severity
 * @property {string[]} customers
 *
 * @typedef {Object} FeedPageMeta
 * @property {string[]} hiddenPosts
 * @property {boolean} isInitialCustomersSelected
 *
 * @typedef {Object} FeedPageState
 * @property {Record<string, FeedPostGeneric>} postsById
 * @property {string[]} postsIds
 * @property {boolean} isLoading
 * @property {FeedPageMeta} meta
 * @property {string | null} error
 * @property {FeedPageFilters} filters
 */

/** @type {FeedPageState} */
const initialFeedState = {
  postsById: {},
  postsIds: [],
  error: null,
  isLoading: true,
  meta: {
    hiddenPosts: [],
    isInitialCustomersSelected: false
  },
  filters: {
    customers: [],
    severity: ["Warning", "Critical"]
  }
};

/**
 * @param {FeedPageState} state
 * @param {import("redux").Action & { payload?: any }} action
 * @returns {FeedPageState}
 */
const feedReducer = (state = initialFeedState, action) => {
  switch (action.type) {
    case "FEED_DATA_REQUEST":
      const posts = filterOutFeedPostsByCondition(
        state.postsById,
        post => !state.meta.hiddenPosts.includes(post.post_id)
      );

      return {
        ...state,
        isLoading: true,
        postsById: posts,
        postsIds: Object.keys(posts),
        meta: {
          ...state.meta,
          hiddenPosts: []
        }
      };
    case "FEED_DATA_FAILED":
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
    case "FEED_DATA_SUCCESS":
      return {
        ...state,
        error: null,
        isLoading: false,
        postsById: action.payload.postsById,
        postsIds: action.payload.postsIds,
        meta: {
          ...state.meta,
          hiddenPosts: []
        }
      };
    case "SET_FEED_FILTER": {
      return {
        ...state,
        meta: {
          ...state.meta,
          hiddenPosts: [],
          /** Check for initial customer selection and disable it after first select */
          isInitialCustomersSelected:
            typeof action.payload.customers === "undefined"
              ? state.meta.isInitialCustomersSelected
              : true
        },
        filters: {
          ...state.filters,
          ...action.payload
        }
      };
    }
    case "RESET_FEED_STATE":
      return { ...initialFeedState };
    case "SET_SHOULD_REFRESH":
      return {
        ...state,
        postsById: {},
        postsIds: [],
        filters: {
          ...state.filters
        },
        meta: {
          ...state.meta,
          hiddenPosts: []
        }
      };
    case "HIDE_FEED_POST": {
      if (!state.meta.hiddenPosts.includes(action.payload)) {
        const posts = filterOutFeedPostsByCondition(
          state.postsById,
          post => !state.meta.hiddenPosts.includes(post.post_id)
        );

        return {
          ...state,
          postsById: posts,
          postsIds: Object.keys(posts),
          meta: {
            ...state.meta,
            hiddenPosts: [action.payload]
          }
        };
      }

      return state;
    }
    case "RESTORE_FEED_POST": {
      if (state.meta.hiddenPosts.includes(action.payload)) {
        return {
          ...state,
          meta: {
            ...state.meta,
            hiddenPosts: state.meta.hiddenPosts.filter(
              post => post !== action.payload
            )
          }
        };
      }

      return state;
    }
    case "REMOVE_CUSTOMER_POSTS": {
      const posts = filterOutFeedPostsByCondition(
        state.postsById,
        /** Allow post if different customer and is not hidden */
        post => {
          const isNonCustomerPost =
            getCustomerIdFromFeedPost(post) !== action.payload;
          return (
            isNonCustomerPost && !state.meta.hiddenPosts.includes(post.post_id)
          );
        }
      );

      return {
        ...state,
        postsById: posts,
        postsIds: Object.keys(posts),
        filters: {
          ...state.filters,
          customers: state.filters.customers.filter(c => c !== action.payload)
        }
      };
    }
    case "HIDE_BY_POST_TYPE": {
      const posts = filterOutFeedPostsByCondition(
        state.postsById,
        /** Allow post if different post type and is not hidden */
        post => {
          const isDifferentPostType = post.post_type !== action.payload;
          return (
            isDifferentPostType &&
            !state.meta.hiddenPosts.includes(post.post_id)
          );
        }
      );

      return {
        ...state,
        postsById: posts,
        postsIds: Object.keys(posts),
        meta: {
          ...state.meta,
          hiddenPosts: []
        }
      };
    }
    case "HIDE_BY_POST_TYPE_SEVERITY": {
      const posts = filterOutFeedPostsByCondition(
        state.postsById,
        /** Allow post if it has different severity or post type and is not hidden */
        post => {
          const isDifferentSeverityPost =
            post.post_type !== action.payload.post_type ||
            post.post_status !== action.payload.post_status;

          return (
            isDifferentSeverityPost &&
            !state.meta.hiddenPosts.includes(post.post_id)
          );
        }
      );

      return {
        ...state,
        postsById: posts,
        meta: {
          ...state.meta,
          hiddenPosts: []
        },
        postsIds: Object.keys(posts)
      };
    }
    case "UPDATE_CUSTOMER_PREFERENCES": {
      const payload =
        /** @type {import("../types/preferences-customers").CustomerModel[] | undefined} */ (action.payload);

      if (!payload) {
        return state;
      }

      return {
        ...state,
        postsById: {},
        postsIds: [],
        meta: {
          ...state.meta,
          hiddenPosts: []
        },
        filters: {
          ...state.filters,
          customers: state.filters.customers.filter(c => {
            const foundCustomer = payload.find(p => p.customer_name === c);

            if (!foundCustomer) {
              return false;
            }

            return foundCustomer.included_in_feed;
          })
        }
      };
    }
    default:
      return state;
  }
};

function fetchFeedPostsAction() {
  return { type: "FEED_DATA_REQUEST" };
}

/** @param {*} payload */
function fetchFeedPostsFailedAction(payload) {
  return { type: "FEED_DATA_FAILED", payload };
}

/** @param {Pick<FeedPageState, 'postsById' | 'postsIds'>} payload */
function fetchFeedPostsSuccessAction(payload) {
  return { type: "FEED_DATA_SUCCESS", payload };
}

function applyFeedShouldRefresh() {
  return { type: "SET_SHOULD_REFRESH" };
}

/** @param {string} postId */
function hideFeedPost(postId) {
  return {
    type: "HIDE_FEED_POST",
    payload: postId
  };
}

/** @param {string} postId */
function restoreFeedPost(postId) {
  return {
    type: "RESTORE_FEED_POST",
    payload: postId
  };
}

/** @param {Partial<FeedPageState['filters']>} payload */
function applyFeedPostsFilters(payload) {
  return {
    type: "SET_FEED_FILTER",
    payload: payload
  };
}

/** @param {import("../types/preferences-customers").CustomerModel[] | undefined} payload */
function applyUpdateFeedCustomerPreferences(payload) {
  return {
    type: "UPDATE_CUSTOMER_PREFERENCES",
    payload: payload
  };
}

/** @param {string} payload */
function applyHideCustomerFeedPosts(payload) {
  return {
    type: "REMOVE_CUSTOMER_POSTS",
    payload: payload
  };
}

/** @param {FeedPostGeneric['post_type']} payload */
function applyHideByPostType(payload) {
  return {
    type: "HIDE_BY_POST_TYPE",
    payload: payload
  };
}

/** @param {Pick<FeedPostGeneric, 'post_type' | 'post_status'>} payload */
function applyHideByPostTypeSeverity(payload) {
  return {
    type: "HIDE_BY_POST_TYPE_SEVERITY",
    payload: payload
  };
}

function applyResetFeedState() {
  return { type: "RESET_FEED_STATE" };
}

/**
 * @template TSelect
 * @param {(state: FeedPageState) => TSelect} select
 * @returns {TSelect}
 */
function useFeedPostsState(select) {
  return useSelector((/** @type {{ feed: FeedPageState }} */ state) =>
    select(state.feed)
  );
}

export {
  feedReducer,
  hideFeedPost,
  restoreFeedPost,
  useFeedPostsState,
  fetchFeedPostsAction,
  applyHideByPostType,
  applyHideByPostTypeSeverity,
  applyResetFeedState,
  applyFeedPostsFilters,
  applyFeedShouldRefresh,
  applyHideCustomerFeedPosts,
  fetchFeedPostsFailedAction,
  applyUpdateFeedCustomerPreferences,
  fetchFeedPostsSuccessAction
};
